import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";

const STORAGE_PREFIX = "atw-hub-pane-width:";
const KEY_STEP = 16;
/** Fired on the layout so every handle in it re-aligns after any pane changes width. */
const RESIZE_EVENT = "atw-pane-resize";

type PaneResizerProps = {
  /** Remembered per device, e.g. "mail.nav". */
  id: string;
  /** Custom property set on the parent layout and read by its grid/flex width. */
  cssVar: string;
  min: number;
  max: number;
  /**
   * "right": resizes the previous sibling pane from its right edge.
   * "left": resizes the next sibling pane from its left edge.
   */
  edge?: "right" | "left";
  /** Smallest width left for the parent's `[data-pane-main]` child. */
  mainMin?: number;
  label: string;
};

function readSaved(id: string): number | null {
  try {
    const value = Number(localStorage.getItem(STORAGE_PREFIX + id));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeSaved(id: string, width: number | null) {
  try {
    if (width == null) localStorage.removeItem(STORAGE_PREFIX + id);
    else localStorage.setItem(STORAGE_PREFIX + id, String(Math.round(width)));
  } catch {
    /* private mode — width still applies for this visit */
  }
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

/**
 * Drag handle between side-by-side panes (desktop and tablet; hidden on phones).
 * Render it as a direct child of a positioned layout, next to the pane it resizes.
 */
export function PaneResizer({
  id,
  cssVar,
  min,
  max,
  edge = "right",
  mainMin = 360,
  label,
}: PaneResizerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<{ startX: number; startWidth: number; maxWidth: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [width, setWidth] = useState<number | null>(null);

  const parts = useCallback(() => {
    const handle = ref.current;
    const layout = handle?.parentElement ?? null;
    const pane = (edge === "right" ? handle?.previousElementSibling : handle?.nextElementSibling) as
      | HTMLElement
      | null
      | undefined;
    const main = layout?.querySelector<HTMLElement>(":scope > [data-pane-main]") ?? null;
    return { handle, layout, pane: pane ?? null, main };
  }, [edge]);

  const roomFor = useCallback(
    (paneWidth: number) => {
      const { main } = parts();
      const mainWidth = main?.getBoundingClientRect().width ?? Infinity;
      return Math.min(max, paneWidth + Math.max(0, mainWidth - mainMin));
    },
    [max, mainMin, parts],
  );

  const position = useCallback(() => {
    const { handle, pane } = parts();
    if (!handle || !pane) return;
    const x = edge === "right" ? pane.offsetLeft + pane.offsetWidth : pane.offsetLeft;
    handle.style.left = `${x}px`;
  }, [edge, parts]);

  const apply = useCallback(
    (next: number | null) => {
      const { layout } = parts();
      if (!layout) return;
      if (next == null) layout.style.removeProperty(cssVar);
      else layout.style.setProperty(cssVar, `${Math.round(next)}px`);
      setWidth(next == null ? null : Math.round(next));
      layout.dispatchEvent(new Event(RESIZE_EVENT));
    },
    [cssVar, parts, position],
  );

  useLayoutEffect(() => {
    const { handle, layout, pane, main } = parts();
    if (!handle || !layout || !pane) return;
    const saved = readSaved(id);
    if (saved) apply(clamp(saved, min, max));

    // Keep a remembered width from squeezing the main pane when the window narrows,
    // and grow back toward it when there is room again.
    const fit = () => {
      const wanted = readSaved(id);
      if (!wanted || drag.current || !main) return;
      const paneWidth = pane.getBoundingClientRect().width;
      const next = clamp(Math.min(wanted, paneWidth + main.getBoundingClientRect().width - mainMin), min, max);
      if (Math.abs(next - paneWidth) >= 1) apply(next);
    };
    const fitNow = () => {
      fit();
      position();
    };
    fitNow();

    let frame = 0;
    const observer = new ResizeObserver(() => {
      position();
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    observer.observe(layout);
    for (const child of Array.from(layout.children)) {
      if (child !== handle) observer.observe(child);
    }
    layout.addEventListener(RESIZE_EVENT, position);
    window.addEventListener("resize", fitNow);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      layout.removeEventListener(RESIZE_EVENT, position);
      window.removeEventListener("resize", fitNow);
    };
  }, [apply, id, mainMin, max, min, parts, position]);

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    const { pane } = parts();
    if (!pane) return;
    event.preventDefault();
    const startWidth = pane.getBoundingClientRect().width;
    drag.current = { startX: event.clientX, startWidth, maxWidth: roomFor(startWidth) };
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* pointer already released */
    }
    document.body.classList.add("pane-resizing");
    setDragging(true);
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    const state = drag.current;
    if (!state) return;
    const dx = event.clientX - state.startX;
    const next = edge === "right" ? state.startWidth + dx : state.startWidth - dx;
    apply(clamp(next, min, Math.max(min, state.maxWidth)));
  }

  function endDrag(event: PointerEvent<HTMLDivElement>) {
    if (!drag.current) return;
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    document.body.classList.remove("pane-resizing");
    setDragging(false);
    const { pane } = parts();
    if (pane) writeSaved(id, pane.getBoundingClientRect().width);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    const { layout, pane } = parts();
    if (!layout || !pane) return;
    event.preventDefault();
    // Prefer the applied value — the shell sidebar animates, so its box lags behind.
    const current = parseFloat(layout.style.getPropertyValue(cssVar)) || pane.getBoundingClientRect().width;
    const grow = (event.key === "ArrowRight") === (edge === "right");
    const next = clamp(current + (grow ? KEY_STEP : -KEY_STEP), min, Math.max(min, roomFor(current)));
    apply(next);
    writeSaved(id, next);
  }

  function reset() {
    apply(null);
    writeSaved(id, null);
  }

  return (
    <div
      ref={ref}
      className={`pane-resizer desktop-only${dragging ? " is-dragging" : ""}`}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={width ?? undefined}
      title={`${label} — drag to resize, double-click to reset`}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
      onDoubleClick={reset}
    />
  );
}
